import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import HPageContainer from "../../../hcomponents/HPageContainer";
import HHeader from "../../../hcomponents/HHeader";
import HActionButton from "../../../hcomponents/HActionButton";
// import HStatCard from "../../../hcomponents/HStatCard"; // COMMENTED OUT: Stats section removed
import { Plus, Banknote, List, Download, Calendar, FileText, Clock, CheckCircle, XCircle } from "lucide-react";
import billingService from "../../../services/billingService";
import * as localService from "../../../services/localService";
import toastUtils from "../../../utils/toast";
import { showApiMessage } from "../../../utils/messageHandler";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../shadcn/ui/dialog";
import { FeatureAccess } from "../../../utils/featureAccessControl";
import StatementRequestDialog from "../../../components/StatementRequestDialog";
import HConfirmDialog from "../../../hcomponents/HConfirmDialog";
import { useUserCurrency } from "../../../hooks/useUserCurrency";
import logger from "../../../utils/logger";


const HostInvoice = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [_statistics, setStatistics] = useState(null); // COMMENTED OUT: Stats section removed
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [statementRequests, setStatementRequests] = useState([]);
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [showInvoiceDetailsModal, setShowInvoiceDetailsModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const history = useHistory();
  const userCurrencyCode = useUserCurrency();

  // Get current user for feature restrictions
  const currentUser = localService.getUser();

  // Use centralized feature access control
  const canManageInvoices = FeatureAccess.canManageInvoices(currentUser);
  const _shouldShowLimitedStats = FeatureAccess.shouldShowLimitedStats(currentUser); // COMMENTED OUT: Stats section removed

  logger.log("User can manage invoices:", canManageInvoices);

  // Debug invoices state
  useEffect(() => {
    logger.log("Invoices state updated:", invoices);
  }, [invoices]);

  // Fetch invoices and statistics
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Prefer real transactions endpoint for recent feed
        const currentUser = localService.getUser?.() || null;
        const orgId = currentUser?.organization?.id;

        // Fetch transactions via getTransactions
        try {
          /* 
          // Previous implementation using getInvoices with providerId (Stored for reference):
          const txResp = await billingService.getInvoices({
            providerId: orgId,
            page: 1,
            limit: 5,
            orderBy: "createdAt:desc",
          });
          */

          const txResp = await billingService.getTransactions({
            organizationId: orgId,
            page: 1,
            limit: 5,
            orderBy: "createdAt:desc",
          });

          let transactions = [];
          if (txResp.status === 200) {
            const rows = txResp.data?.data || txResp.data || [];
            transactions = rows.map((row, idx) => {
              // Map transaction fields based on actual transaction schema
              const isCredit = row.type === "CREDIT";
              const label = row.description || row.narration || "Transaction";
              const amount = parseFloat(row.amount || row.value || 0);
              const reference = row.reference || row.invoiceReference || row.referenceCode || `TX-${row.id || idx}`;
              const companyName = row.company || row.organization?.name || currentUser?.organization?.name || "—";
              return {
                id: row.id || idx,
                date: row.createdAt || row.date || "-",
                description: label,
                company: companyName,
                reference,
                amount,
                type: isCredit ? "credit" : "debit",
              };
            });

            setRecentTransactions(transactions);

            // Statistics from fetched transactions
            const totalCredits = transactions
              .filter(t => t.type === "credit")
              .reduce((sum, t) => sum + (t.amount || 0), 0);
            const totalDebits = transactions
              .filter(t => t.type === "debit")
              .reduce((sum, t) => sum + (t.amount || 0), 0);
            setStatistics({
              totalRevenue: totalCredits,
              outstandingAmount: Math.max(totalDebits - totalCredits, 0),
              overstayCharges: 0,
            });
          }
        } catch (txError) {
          logger.error("Error fetching transactions:", txError);
        }

        try {
          logger.log("Calling getInvoices with providerId for orgId:", orgId);
          const invoicesResp = await billingService.getInvoices({
            providerId: orgId,
            page: 1,
            limit: 5,
            orderBy: "createdAt:desc",
            includeRelations: true,
          });
          logger.log("Invoices API Response:", invoicesResp);
          if (invoicesResp.status === 200) {
            const rows = invoicesResp.data?.data || invoicesResp.data || [];
            logger.log("Invoices data:", rows);
            setInvoices(rows);
          } else {
            logger.log("Invoices API failed with status:", invoicesResp.status);
          }
        } catch (invoiceError) {
          logger.error("Error fetching invoices:", invoiceError);
        }

        // Fetch statement requests
        try {
          const statementResp = await billingService.getStatementRequests({
            organizationId: orgId,
            page: 1,
            limit: 10,
            orderBy: "createdAt:desc",
          });
          if (statementResp.status === 200) {
            const rows = statementResp.data?.data || statementResp.data || [];
            setStatementRequests(rows);
          }
        } catch (statementError) {
          logger.error("Error fetching statement requests:", statementError);
        }

        setLoading(false);
      } catch (error) {
        logger.error("Error in fetchData:", error);
        setLoading(false);
        showApiMessage(error, "load billing data");
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateInvoice = () => {
    history.push("/host/add-invoice");
  };

  const handleViewAllInvoices = () => {
    // Navigate to detailed invoice list view (you can create this page later)
    history.push("/host/invoice/list");
  };

  const handleViewInvoice = async invoiceId => {
    try {
      // Fetch full invoice details
      const response = await billingService.getInvoiceById(invoiceId);
      if (response.status === 200) {
        const invoiceData = response.data?.data || response.data;
        setSelectedInvoice(invoiceData);
        setShowInvoiceDetailsModal(true);
      }
    } catch (error) {
      logger.error("Error fetching invoice details:", error);
      showApiMessage(error, "load invoice");
    }
  };

  const handleViewAllTransactions = () => {
    history.push("/host/invoice/list?tab=transactions");
  };

  const handleExportTransactions = () => {
    // Implement export functionality
    toastUtils.success("Export started", {
      description: "Your transaction report will be ready shortly.",
    });
  };

  const handleStatementRequestSuccess = async () => {
    const currentUser = localService.getUser?.() || null;
    const orgId = currentUser?.organization?.id;

    // Refresh statement requests
    try {
      const statementResp = await billingService.getStatementRequests({
        organizationId: orgId,
        page: 1,
        limit: 10,
        orderBy: "createdAt:desc",
      });
      if (statementResp.status === 200) {
        const rows = statementResp.data?.data || statementResp.data || [];
        setStatementRequests(rows);
      }
    } catch (error) {
      logger.error("Error refreshing statement requests:", error);
    }
  };

  const getStatementStatusIcon = status => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "PENDING":
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case "FAILED":
        return <XCircle className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatementStatusColor = status => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "bg-green-100 text-green-700";
      case "PENDING":
        return "bg-yellow-100 text-yellow-700";
      case "FAILED":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const formatCurrency = amount => {
    const currency = userCurrencyCode || "KES";
    return `${currency} ${amount.toLocaleString()}`;
  };

  const addNewButton = canManageInvoices ? (
    <HActionButton
      icon={<Plus className="w-4 h-4" />}
      label="Create Invoice"
      onClick={handleCreateInvoice}
      variant="primary"
    />
  ) : null;

  if (loading) {
    return (
      <HPageContainer>
        <HHeader rightButton={addNewButton} />
        <div className="overflow-auto flex-1 p-4 sm:p-6 md:p-8">
          <div className="space-y-6 animate-pulse">
            <div className="mb-8 w-64 h-8 rounded bg-slate-200"></div>
            <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-3">
              <div className="h-24 rounded bg-slate-200"></div>
              <div className="h-24 rounded bg-slate-200"></div>
              <div className="h-24 rounded bg-slate-200"></div>
            </div>
            <div className="h-96 rounded bg-slate-200"></div>
          </div>
        </div>
      </HPageContainer>
    );
  }

  return (
    <HPageContainer>
      <HHeader rightButton={addNewButton} />

      <div className="overflow-auto flex-1 p-4 sm:p-6 md:p-8">
        {/* Header */}

        {/* Summary Cards - COMMENTED OUT: Not connected to anything */}
        {/* <div className={`grid grid-cols-1 gap-6 mb-8 ${shouldShowLimitedStats ? "md:grid-cols-2" : "md:grid-cols-3"}`}>
          <HStatCard
            title="Total Revenue"
            value={formatCurrency(statistics?.totalRevenue || 8750)}
            details="Monthly earnings"
            detailsColor="text-green-600"
          />
          {shouldShowLimitedStats ? (
            <HStatCard
              title="Total Bookings"
              value={recentTransactions?.length || 0}
              details="Total transactions"
              detailsColor="text-blue-600"
            />
          ) : (
            <>
              <HStatCard
                title="Outstanding Invoices"
                value={formatCurrency(statistics?.outstandingAmount || 10500)}
                details="Pending payments"
                detailsColor="text-orange-600"
              />
              <HStatCard
                title="Overstay Charges Generated"
                value={formatCurrency(statistics?.overstayCharges || 750)}
                details="Additional charges"
                detailsColor="text-blue-600"
              />
            </>
          )}
        </div> */}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Recent Transactions */}
          {recentTransactions && recentTransactions.length > 0 && (
            <div className="p-6 bg-white rounded-lg border border-gray-200 lg:col-span-2">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Recent Transactions</h2>
                <button
                  className="text-sm font-medium text-blue-600 hover:underline"
                  onClick={handleViewAllTransactions}
                >
                  View All
                </button>
              </div>
              <div className="space-y-6">
                {recentTransactions.map(transaction => (
                  <div key={transaction.id} className="flex justify-between items-center">
                    <div>
                      <div className="text-sm text-gray-500">{transaction.date}</div>
                      <div className="font-medium text-gray-900">{transaction.description}</div>
                      <div className="text-sm text-gray-600">
                        {transaction.company} <span className="text-gray-500">Ref: {transaction.reference}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <div
                        className={`font-semibold ${transaction.type === "credit" ? "text-green-600" : "text-red-600"}`}
                      >
                        {transaction.type === "credit" ? "+" : "-"} {formatCurrency(transaction.amount)}
                      </div>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wide font-medium">
                        {transaction.type}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="p-6 bg-white rounded-lg border border-gray-200 lg:col-span-1">
            <h2 className="mb-4 text-xl font-semibold text-gray-900">Quick Actions</h2>
            <div className="space-y-3">
              {canManageInvoices && (
                <>
                  <HActionButton
                    icon={<Plus className="w-5 h-5" />}
                    label="Generate New Invoice"
                    onClick={handleCreateInvoice}
                    className="w-full"
                  />
                  <button
                    className="flex justify-center items-center px-4 py-3 w-full text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50"
                    onClick={handleViewAllInvoices}
                  >
                    <List className="mr-2 w-5 h-5" />
                    View All Invoices
                  </button>
                </>
              )}
              <button
                className="flex justify-center items-center px-4 py-3 w-full text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50"
                onClick={() => setShowStatementModal(true)}
              >
                <FileText className="mr-2 w-5 h-5" />
                Request Statement
              </button>
              {/* <button
                className="flex justify-center items-center px-4 py-3 w-full text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50"
                onClick={handleExportTransactions}
              >
                <Download className="mr-2 w-5 h-5" />
                Export Transactions
              </button> */}
            </div>
          </div>

          {/* Recent Invoices */}
          <div className="p-6 bg-white rounded-lg border border-gray-200 lg:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Recent Invoices</h2>
              <button className="text-sm font-medium text-blue-600 hover:underline" onClick={handleViewAllInvoices}>
                View All
              </button>
            </div>
            <div className="space-y-6">
              {invoices && invoices.length > 0 ? (
                invoices.map(inv => {
                  const created = inv.createdAt || inv.serviceDate;
                  const amount = parseFloat(inv.amount || 0);
                  const status = (inv.status || "").toString().toUpperCase();
                  const badge = (
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        status === "PAID"
                          ? "bg-green-100 text-green-700"
                          : status === "PENDING"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-red-100 text-red-700"
                      }`}
                    >
                      {status || "—"}
                    </span>
                  );
                  return (
                    <div
                      key={inv.id}
                      className="flex justify-between items-center p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => handleViewInvoice(inv.id)}
                    >
                      <div>
                        <div className="text-sm text-gray-500">
                          {created ? new Date(created).toLocaleDateString() : "-"}
                        </div>
                        <div className="font-medium text-gray-900">{inv.referenceCode || `INV-${inv.id}`}</div>
                        <div className="text-sm text-gray-600">{inv.description || "Invoice"}</div>
                      </div>
                      <div className="flex gap-3 items-center">
                        <div className="font-semibold text-gray-900">{formatCurrency(isNaN(amount) ? 0 : amount)}</div>
                        {badge}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <Calendar className="mx-auto mb-2 w-8 h-8 opacity-50" />
                  <p>No invoices yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Statement Requests */}
          <div className="p-6 bg-white rounded-lg border border-gray-200 lg:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Statement Requests</h2>
              <button
                className="text-sm font-medium text-blue-600 hover:underline"
                onClick={() => setShowStatementModal(true)}
              >
                Request Statement
              </button>
            </div>
            <div className="space-y-4">
              {statementRequests && statementRequests.length > 0 ? (
                statementRequests.slice(0, 5).map(request => {
                  const created = request.createdAt;
                  const status = request.completed ? "COMPLETED" : "PENDING";
                  return (
                    <div
                      key={request.id}
                      className="flex justify-between items-center p-3 rounded-lg border border-gray-200"
                    >
                      <div className="flex items-center space-x-3">
                        {getStatementStatusIcon(status)}
                        <div>
                          <div className="text-sm text-gray-500">
                            {created ? new Date(created).toLocaleDateString() : "-"}
                          </div>
                          <div className="font-medium text-gray-900">{request.recipientEmail}</div>
                          <div className="text-sm text-gray-600">
                            {request.startDate} - {request.endDate}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatementStatusColor(status)}`}>
                          {status}
                        </span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="py-8 text-center text-gray-500">
                  <FileText className="mx-auto mb-2 w-8 h-8 opacity-50" />
                  <p>No statement requests yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Statement Request Dialog */}
      <StatementRequestDialog
        open={showStatementModal}
        onOpenChange={setShowStatementModal}
        onSuccess={handleStatementRequestSuccess}
      />

      {/* Invoice Details Modal */}
      <Dialog open={showInvoiceDetailsModal} onOpenChange={setShowInvoiceDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-6">
              {/* Invoice Header Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Invoice Number</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {selectedInvoice.referenceCode || `INV-${selectedInvoice.id}`}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <span
                    className={`inline-block px-2 py-1 text-xs rounded-full ${
                      selectedInvoice.status === "PAID"
                        ? "bg-green-100 text-green-700"
                        : selectedInvoice.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                    }`}
                  >
                    {selectedInvoice.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Invoice Date</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedInvoice.createdAt
                      ? new Date(selectedInvoice.createdAt).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Amount</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatCurrency(parseFloat(selectedInvoice.amount || 0))}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Description</p>
                <p className="text-sm text-gray-900">{selectedInvoice.description || "No description provided"}</p>
              </div>

              {/* Organization Info */}
              {selectedInvoice.organization && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Organization</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedInvoice.organization.name}</p>
                  <p className="text-xs text-gray-600 mt-1">{selectedInvoice.organization.contactEmail}</p>
                  {selectedInvoice.organization.contactMsisdn && (
                    <p className="text-xs text-gray-600">{selectedInvoice.organization.contactMsisdn}</p>
                  )}
                </div>
              )}

              {/* Service Package Info */}
              {selectedInvoice.servicePackage && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600 mb-2">Service Package</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedInvoice.servicePackage.name}</p>
                  <p className="text-xs text-gray-600 mt-1">{selectedInvoice.servicePackage.description}</p>
                </div>
              )}

              {/* Currency Info */}
              {selectedInvoice.currency && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Currency</p>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedInvoice.currency.name} ({selectedInvoice.currency.code})
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Product Type</p>
                    <p className="text-sm font-medium text-gray-900">{selectedInvoice.productType || "N/A"}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowInvoiceDetailsModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowInvoiceDetailsModal(false);
                    history.push(`/host/invoice/${selectedInvoice.id}`);
                  }}
                  className="flex-1 px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                >
                  View Full Invoice
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </HPageContainer>
  );
};

export default HostInvoice;
